import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, TreePine, BedDouble, Maximize, MapPin, Check } from "lucide-react";
import api from "../lib/api";
import { SERVICES } from "../data/services";
import InquiryForm from "../components/InquiryForm";
import { Button } from "../components/ui/button";

const HERO_IMG =
  "https://images.unsplash.com/photo-1609319172668-8b4f021f3b7b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwyfHx3b29kZW4lMjB2aWxsYSUyMG5hdHVyZXxlbnwwfHx8fDE3ODM5MTk5NDJ8MA&ixlib=rb-4.1.0&q=85";

const STATS = [
  { value: "50+", label: "Villas Managed" },
  { value: "100%", label: "Upkeep Ownership" },
  { value: "12", label: "Years of Craft" },
  { value: "₹200Cr+", label: "Property Transacted" },
];

// Bento spans for a "Tetris" style services layout
const bentoSpan = (i) => (i === 0 ? "md:col-span-2 md:row-span-2" : "md:col-span-2");

export default function Home() {
  const [properties, setProperties] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/properties").then(({ data }) => setProperties(data)).catch(() => {});
    if (window.location.hash) {
      const id = window.location.hash.slice(1);
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 200);
    }
  }, []);

  const scrollToContact = () =>
    document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative h-[92vh] min-h-[560px] w-full overflow-hidden">
        <img
          src={HERO_IMG}
          alt="Pine wooden villa in the hills"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/35 to-black/40" />
        <div className="noise-overlay absolute inset-0 opacity-20 mix-blend-overlay" />

        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 h-full flex flex-col justify-center">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-xs tracking-[0.25em] uppercase font-semibold text-accent-foreground/90 bg-accent/80 w-fit px-4 py-1.5 rounded-full mb-6"
          >
            Imported Pine Wooden Villas
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-heading text-5xl sm:text-6xl lg:text-7xl font-light text-white leading-[1.02] max-w-3xl tracking-tight"
          >
            Live amongst the pines, in a villa built to last.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="text-lg text-white/85 max-w-xl mt-6 leading-relaxed"
          >
            LAVista designs, builds and manages eco-luxury wooden villas — while handling
            buying, selling, legal and everything in between.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="flex flex-wrap gap-4 mt-9"
          >
            <Button
              data-testid="hero-explore-btn"
              onClick={() => document.getElementById("villas")?.scrollIntoView({ behavior: "smooth" })}
              className="rounded-full h-12 px-7 text-base gap-2"
            >
              Explore Villas <ArrowRight className="h-4 w-4" />
            </Button>
            <Button
              data-testid="hero-contact-btn"
              variant="outline"
              onClick={scrollToContact}
              className="rounded-full h-12 px-7 text-base bg-white/10 text-white border-white/40 hover:bg-white/20 hover:text-white"
            >
              Talk to us
            </Button>
          </motion.div>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12 grid grid-cols-2 md:grid-cols-4 gap-8">
          {STATS.map((s) => (
            <div key={s.label} data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="font-heading text-4xl sm:text-5xl font-light text-accent">{s.value}</div>
              <div className="text-sm text-primary-foreground/70 mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section id="services" className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
        <div className="max-w-2xl mb-14">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-accent">What we do</p>
          <h2 className="font-heading text-4xl sm:text-5xl font-light tracking-tight mt-3">
            Everything your property needs, under one roof.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 auto-rows-[240px] gap-5">
          {SERVICES.map((svc, i) => {
            const Icon = svc.icon;
            const isFeature = i === 0;
            return (
              <motion.div
                key={svc.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
                data-testid={`service-card-${svc.id}`}
                className={`${bentoSpan(i)} group relative overflow-hidden rounded-lg border border-border bg-card ${
                  isFeature ? "" : "flex flex-col"
                }`}
              >
                {isFeature ? (
                  <>
                    <img src={svc.image} alt={svc.title} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                    <div className="relative z-10 h-full flex flex-col justify-end p-8">
                      <Icon className="h-8 w-8 text-accent mb-3" strokeWidth={1.5} />
                      <h3 className="font-heading text-3xl font-light text-white">{svc.title}</h3>
                      <p className="text-white/80 mt-2 max-w-md">{svc.short}</p>
                    </div>
                  </>
                ) : (
                  <div className="p-7 flex flex-col h-full">
                    <Icon className="h-7 w-7 text-accent mb-4" strokeWidth={1.5} />
                    <h3 className="font-heading text-2xl font-normal text-card-foreground">{svc.title}</h3>
                    <p className="text-xs tracking-wide uppercase text-accent/80 mt-1 font-semibold">{svc.tagline}</p>
                    <p className="text-sm text-muted-foreground mt-3 leading-relaxed line-clamp-3">{svc.short}</p>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* DESIGN & CONSTRUCTION highlight */}
      <section className="bg-secondary">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-accent">Our specialty</p>
            <h2 className="font-heading text-4xl sm:text-5xl font-light tracking-tight mt-3 text-secondary-foreground">
              Crafted from imported pine wood.
            </h2>
            <p className="text-secondary-foreground/75 mt-5 leading-relaxed text-lg">
              Each LAVista villa is a study in warmth and durability. We source premium
              imported pine and pair it with sustainable construction to create homes that
              age gracefully in nature.
            </p>
            <ul className="mt-7 space-y-3">
              {["Premium imported pine sourcing", "Custom architecture & interiors", "Turnkey delivery & handover", "Value add-ons: EV, horticulture, adventure zones"].map((p) => (
                <li key={p} className="flex items-start gap-3 text-secondary-foreground/85">
                  <Check className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
            <Button data-testid="construction-cta-btn" onClick={scrollToContact} className="rounded-full h-12 px-7 mt-8 gap-2">
              Build with LAVista <ArrowRight className="h-4 w-4" />
            </Button>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <img
              src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
              alt="Wooden villa interior"
              className="rounded-lg w-full h-[460px] object-cover shadow-lg"
            />
            <div className="absolute -bottom-6 -left-6 bg-card border border-border rounded-lg p-5 shadow-lg hidden sm:block">
              <TreePine className="h-6 w-6 text-accent mb-1" />
              <p className="font-heading text-2xl">Pine-first builds</p>
              <p className="text-xs text-muted-foreground">Warmth that lasts generations</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* VILLAS */}
      <section id="villas" className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-12">
          <div className="max-w-xl">
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-accent">Featured</p>
            <h2 className="font-heading text-4xl sm:text-5xl font-light tracking-tight mt-3">
              Villas & land, ready for you.
            </h2>
          </div>
          <div className="flex gap-3">
            <Button data-testid="villas-book-btn" onClick={() => navigate("/villas")} className="rounded-full">
              Book a Villa
            </Button>
            <Button variant="outline" data-testid="villas-list-btn" onClick={() => navigate("/list-your-villa")} className="rounded-full">
              List Your Villa
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
          {properties.map((p, i) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              data-testid={`property-card-${p.id}`}
              className="group rounded-lg overflow-hidden border border-border bg-card"
            >
              <div className="relative h-60 overflow-hidden">
                <img src={p.image} alt={p.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <span className="absolute top-4 left-4 bg-primary text-primary-foreground text-xs font-semibold px-3 py-1 rounded-full">
                  {p.type}
                </span>
              </div>
              <div className="p-6">
                <h3 className="font-heading text-2xl font-normal">{p.title}</h3>
                <p className="flex items-center gap-1.5 text-sm text-muted-foreground mt-1">
                  <MapPin className="h-4 w-4" /> {p.location}
                </p>
                <p className="text-sm text-muted-foreground mt-3 leading-relaxed line-clamp-2">{p.description}</p>
                <div className="flex items-center gap-5 mt-4 text-sm text-foreground/70">
                  {p.bedrooms > 0 && (
                    <span className="flex items-center gap-1.5"><BedDouble className="h-4 w-4 text-accent" /> {p.bedrooms} BR</span>
                  )}
                  <span className="flex items-center gap-1.5"><Maximize className="h-4 w-4 text-accent" /> {p.area}</span>
                </div>
                <div className="flex items-center justify-between mt-5 pt-4 border-t border-border">
                  <span className="font-heading text-2xl text-accent">{p.price}</span>
                  {p.bookable ? (
                    <Button size="sm" variant="ghost" data-testid={`property-book-${p.id}`} onClick={() => navigate(`/villa/${p.id}`)} className="gap-1">
                      Book Now <ArrowRight className="h-4 w-4" />
                    </Button>
                  ) : (
                    <Button size="sm" variant="ghost" data-testid={`property-enquire-${p.id}`} onClick={scrollToContact} className="gap-1">
                      Enquire <ArrowRight className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="bg-secondary">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 grid grid-cols-1 lg:grid-cols-2 gap-14">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-accent">Get in touch</p>
            <h2 className="font-heading text-4xl sm:text-5xl font-light tracking-tight mt-3 text-secondary-foreground">
              Let&apos;s plan your villa journey.
            </h2>
            <p className="text-secondary-foreground/75 mt-5 leading-relaxed text-lg max-w-md">
              Whether you want to list, build, buy or add EV chargers and adventure zones —
              tell us what you need and our team will get back within 24 hours.
            </p>
            <img
              src="https://images.unsplash.com/photo-1700689807667-82630348b301?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwyfHxob3J0aWN1bHR1cmUlMjBnYXJkZW4lMjBsYW5kc2NhcGV8ZW58MHx8fHwxNzgzOTE5OTQxfDA&ixlib=rb-4.1.0&q=85"
              alt="Horticulture landscaping"
              className="rounded-lg w-full h-64 object-cover mt-8 hidden lg:block"
            />
          </div>
          <div className="bg-card border border-border rounded-lg p-8 shadow-sm">
            <InquiryForm />
          </div>
        </div>
      </section>
    </div>
  );
}
